import '@interactjs/auto-start';
import '@interactjs/actions/drag';
import '@interactjs/actions/resize';
import '@interactjs/actions/drop';
import '@interactjs/dev-tools';
import interact from '@interactjs/interact';

export const contentautoselect = {
  mounted(el) {
    const element = $(el);
    element.bind("focus", () => {
      if (element.is("input") || element.is("textarea")) {
        element.select();
      } else {
        const range = document.createRange();
        range.selectNodeContents(element[0]);
        const sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(range);
      }
      // prevent mouseup from unselecting
      element.one("mouseup", (e) => {
        e.preventDefault();
      });
      element.one("mousedown", () => {
        element.off("mouseup");
      });
    });
  },
};

// reload gif animation on element mount
export const reloadSrcOnMount = {
  mounted(el) {
    setTimeout(() => {
      el.src = `${el.getAttribute('src')}?${new Date().getTime()}`;
    });
  },
};

export const draggable = {
  mounted(el, binding) {
    interact(el).draggable(binding.value || {});
  },
  updated(el, binding) {
    if (binding.value && binding.oldValue && binding.value.enabled != binding.oldValue.enabled) {
      interact(el).draggable({
        enabled: binding.value.enabled,
      });
    }
  },
};

export const droppable = {
  mounted(el, binding) {
    interact(el).dropzone(binding.value || {});
  },
  updated(el, binding) {
    if (binding.value && binding.oldValue && binding.value.enabled != binding.oldValue.enabled) {
      interact(el).dropzone({
        enabled: binding.value.enabled,
      });
    }
  },
};

export const resizable = {
  mounted(el, binding) {
    interact(el).resizable(binding.value || {});
  },
  updated(el, binding) {
    if (binding.value && binding.oldValue && binding.value.enabled != binding.oldValue.enabled) {
      interact(el).resizable({
        enabled: binding.value.enabled,
      });
    }
  },
};

export const selectableArea = {
  mounted(el, binding) {
    const options = binding.value || {};
    el.selectableOptions = { enabled: options.enabled, cleanup: () => {} };

    const onMouseMove = (event) => { // move
      if (options.move) options.move(event, el);
    };

    const onMouseUp = (event) => { // end
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);

      if (options.end) options.end(event, el);
    };

    const onMouseDown = (event) => { // start
      if (event.button !== 0) return; // only left click
      if (! el.selectableOptions.enabled) return;
      if (options.start) options.start(event, el);
      event.preventDefault();
      // add listeners dynamically on interaction start
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    };

    el.selectableOptions.cleanup = () => {
      el.removeEventListener('mousedown', onMouseDown);
      // remove dynamic listeners in case they're still active
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      if (options.cleanup) options.cleanup();
    };

    // events - only mousedown is permanent, mousemove/mouseup are added dynamically
    el.addEventListener('mousedown', onMouseDown);
  },

  updated(el, binding) {
    if (binding.value && binding.oldValue && binding.value.enabled != binding.oldValue.enabled) {
      el.selectableOptions.enabled = binding.value.enabled;
    }
  },

  unmounted(el) {
    if (el.selectableOptions.cleanup) el.selectableOptions.cleanup();
  },
};

/**
* Create and drag element from button inside navigation
*/
export const draggableButton = {
  mounted(el, binding) {
    const options = binding.value || {};
    const type = options.type || '';
    const cursorAt = type == 'milestone' ? { top: 12, left: 27 } : type == 'task' ? { top: 5, left: 53 } : { top: el.offsetHeight / 2, left: el.offsetWidth / 2 };
    let dragClone = null;

    function $t(key) {
      if (! binding.instance.$t) return key;
      return binding.instance.$t.call(binding.instance, key);
    }

    function createDragClone(elType) {
      if (elType != 'task' && elType != 'milestone') { // clone el
        dragClone = el.cloneNode(true);
        dragClone.style.zIndex = 9999;
        dragClone.style.transition = 'none';
        dragClone.style.width = `${el.offsetWidth}px`;
        dragClone.style.height = `${el.offsetHeight}px`;
        dragClone.querySelector('.v-ripple__container')?.remove(); // remove vuetify btn effect
        return dragClone;
      }

      const width = (elType == 'milestone') ? 70 : 130;
      const dragCloneEl = document.createElement('article');
      dragCloneEl.className = `element ${elType}`;
      dragCloneEl.style.zIndex = '9999';
      dragCloneEl.style.width = `${width}px`;
      dragCloneEl.style.background = 'none';
      if (elType == 'milestone') {
        dragCloneEl.innerHTML = `<div class="fak fa-bbp-milestone milestone-default-blue" style="font-size:16px; min-height:20px"></div><div style="font-weight:bold">${$t('PLANNING.MILESTONE')}</div>`;
      } else {
        dragCloneEl.innerHTML = `<div class="element-title task-default-blue"><div style="font-weight:bold">${$t('PLANNING.NEW_BUBBLE')}</div></div>`;
      }
      return dragCloneEl;
    }

    interact(el).draggable({
      enabled: options.enabled,
      cursorChecker(action, interactable, element, interacting) {
        return interacting ? 'grabbing' : null;
      },
      listeners: {
        start(event) {
          dragClone = createDragClone(type);
          document.body.appendChild(dragClone);
          event.target.dragData = { dragClone };

          dragClone.style.position = 'fixed';
          dragClone.style.pointerEvents = 'none';
          dragClone.style.left = `${event.client.x - cursorAt.left}px`;
          dragClone.style.top = `${event.client.y - cursorAt.top}px`;
          if (options.start) options.start(event, type);
        },
        move(event) {
          if (dragClone) {
            dragClone.style.left = `${event.client.x - cursorAt.left}px`;
            dragClone.style.top = `${event.client.y - cursorAt.top}px`;
          }
          if (options.move) options.move(event, type);
        },
        end(event) {
          if (! dragClone) return;
          const cloneRect = dragClone.getBoundingClientRect();
          dragClone.remove();
          dragClone = null;
          event.dragData = { x: event.pageX + cloneRect.width / 2 - cursorAt.left, y: event.pageY + cloneRect.height / 2 - cursorAt.top };
          if (options.end) options.end(event, type);
        },
      },
    });
  },
  updated(el, binding) {
    if (binding.value && binding.oldValue && binding.value.enabled != binding.oldValue.enabled) {
      interact(el).draggable({
        enabled: binding.value.enabled,
      });
    }
  },
};

export const inputAutoWidth = {
  mounted(el, binding) {
    const options = binding.value || {};
    el.$span = $('<span style="visibility: hidden; white-space: pre;"></span>');

    el.updateElementWidth = function (text) {
      const element = $(el);
      el.$span.html(text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;'));
      element.append(el.$span);
      element.css('width', el.$span.width() + 30);
      el.$span.remove();
    };
    el.updateElementWidth(options.text);
  },
  updated(el, binding) {
    const options = binding.value || {};
    const oldOptions = binding.oldValue || {};
    if (! options.text || options.text == oldOptions.text) return;
    el.updateElementWidth(options.text);
  },
};

export const fixToElement = {
  mounted(element, binding) {
    const options = binding.value || {};
    let target;

    const updateElementPosition = () => {
      if (! target) return;
      const targetRect = target.getBoundingClientRect();
      if (typeof options.top == 'number') {
        const topbarMarginIfTopbarExists = 'min(var(--v-layout-top), 1px) * 15'; // 0px (if --v-layout-top == 0px) or 15px (navigation pb-4 - 1px for overlap)
        element.style.top = `max(calc(var(--v-layout-top) + ${topbarMarginIfTopbarExists}), ${targetRect.top + options.top}px)`;
      }
      if (typeof options.left == 'number') element.style.left = `${targetRect.left + options.left}px`;
      if (typeof options.right == 'number') element.style.right = `${targetRect.right - targetRect.width + options.right}px`;
      if (typeof options.bottom == 'number') element.style.bottom = `calc(100vh - min(${targetRect.bottom - options.bottom}px, 100vh))`;
    };

    element.style.position = 'fixed';

    setTimeout(() => {
      target = document.querySelector(options.target);
      if (! target) return;
      updateElementPosition();
    });

    setTimeout(() => { // second call to make sure all animations have finished
      if (! target) target = document.querySelector(options.target);
      if (! target) return;
      updateElementPosition();

      // set up observers
      element.onScroll = updateElementPosition;
      window.addEventListener('scroll', updateElementPosition);

      // height observer for target
      element.heightObserver = new ResizeObserver(updateElementPosition);
      element.heightObserver.observe(target);
    }, 1000);
  },
  unmounted(element) {
    if (element.onScroll) window.removeEventListener('scroll', element.onScroll);
    if (element.heightObserver) element.heightObserver.disconnect();
  },
};

export const fixToTop = {
  mounted(element, binding) {
    let offsetTop;
    let startTop;
    let height;
    let width;
    let placeholder;
    const options = binding.value || {};
    const xScrollElement = options.xScroll && document.querySelector(options.xScroll);

    if (options.placeholder) placeholder = element.parentNode.querySelector(`.${options.placeholder}`);
    let elementReady = false;

    function updateClip() {
      if (element.classList.contains('fixed') && xScrollElement) {
        const xscroll = xScrollElement.scrollLeft;
        const marginLeft = xscroll + document.documentElement.scrollLeft;
        element.style.transform = `translate3d(${-marginLeft}px, 0, 0)`;
        element.style['clip-path'] = `polygon(${xscroll}px 0, ${width + xscroll}px 0, ${width + xscroll}px ${height + 10}px, ${xscroll}px ${height + 10}px)`;
      } else {
        element.style.transform = 'none';
        element.style['clip-path'] = 'none';
      }
      if (element.classList.contains('fixed')) {
        element.style.top = `${offsetTop}px`;
      } else {
        element.style.top = null;
      }
    }

    function updateElementVars() {
      const elementHeight = element.offsetHeight;
      if (! elementHeight) return; // planning not visible on screen
      offsetTop = options.topbar && document.querySelector(options.topbar)?.offsetHeight || 0;
      const elementOffsetTop = element.getBoundingClientRect().top + document.documentElement.scrollTop; // element.offset().top
      startTop = Math.max(0, elementOffsetTop - offsetTop);
      height = elementHeight;
      width = xScrollElement ? Math.min(xScrollElement.offsetWidth, element.offsetWidth) : element.offsetWidth;
      if (placeholder) {
        placeholder.style.display = 'none';
        placeholder.style.height = `${height}px`;
      }
      elementReady = height > 0;
    }

    if (xScrollElement) {
      xScrollElement.addEventListener('scroll', updateClip);
    }

    function onScroll() {
      if (! elementReady) return;
      if (document.documentElement.scrollTop > startTop) {
        if (! element.classList.contains('fixed')) updateElementVars();
        element.classList.add('fixed');
        if (placeholder) placeholder.style.display = 'block';
      } else {
        element.classList.remove('fixed');
        if (placeholder) placeholder.style.display = 'none';
      }
      updateClip();
    }

    window.addEventListener('scroll', onScroll);

    function init() {
      updateElementVars();
      if (elementReady) {
        onScroll();
      } else {
        setTimeout(init, 300);
      }
    }
    setTimeout(init);
    element.onScroll = onScroll;
  },
  unmounted(element) {
    window.removeEventListener('scroll', element.onScroll);
  },
};
